import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Icon } from '../components/ui/Icons'
import PasswordField from '../components/ui/PasswordField'
import { cambiarClaveInicial } from '../services/usuariosService'

const requirements = [
  ['Al menos 8 caracteres', (value) => value.length >= 8],
  ['Una letra mayúscula', (value) => /[A-ZÁÉÍÓÚÑ]/.test(value)],
  ['Una letra minúscula', (value) => /[a-záéíóúñ]/.test(value)],
  ['Un número', (value) => /\d/.test(value)],
]

// Cambio obligatorio de la contraseña temporal. Sin sidebar ni navegación:
// solo se puede cambiar la contraseña o cerrar sesión.
export default function CambiarClaveInicialPage() {
  const { authUser, debeCambiarClave, refreshProfile, signOut } = useAuth()
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  // Al quedar debe_cambiar_clave = false (tras refrescar el perfil) se continúa al Dashboard.
  if (!debeCambiarClave) return <Navigate to="/dashboard" replace />

  const unmet = requirements.filter(([, test]) => !test(password))

  async function submit(event) {
    event.preventDefault()
    if (!password) return setError('Ingresa una nueva contraseña.')
    if (unmet.length) return setError('La contraseña no cumple los requisitos.')
    if (password !== confirmation) return setError('Las contraseñas no coinciden.')
    setSaving(true); setError('')
    try {
      await cambiarClaveInicial(password)
    } catch (changeError) {
      // 409: el cambio ya se había completado; se refresca el perfil igualmente.
      if (changeError.status !== 409) {
        setError(changeError.message)
        setSaving(false)
        return
      }
    }
    try {
      const access = await refreshProfile()
      if (access.cpUsuario?.debe_cambiar_clave) {
        setError('No fue posible confirmar el cambio. Intenta nuevamente.')
        setSaving(false)
      }
      // Si quedó en false, el render siguiente navega a /dashboard.
    } catch (refreshError) {
      setError(refreshError.message)
      setSaving(false)
    }
  }

  async function logout() {
    try { await signOut() } catch (signOutError) { setError(signOutError.message) }
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-background px-4 py-safe-page">
      <section className="w-full max-w-md rounded-3xl border border-border bg-surface px-5 py-7 shadow-xl shadow-primary/8 sm:px-9 sm:py-9">
        <div className="text-center">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary"><Icon name="key" className="h-7 w-7" /></span>
          <h1 className="mt-5 text-2xl font-bold tracking-tight sm:text-3xl">Cambia tu contraseña</h1>
          <p className="mt-3 text-text-secondary">Por seguridad debes cambiar la contraseña temporal antes de continuar.</p>
          {authUser?.email && <p className="mt-2 break-words text-sm font-medium text-text-primary">{authUser.email}</p>}
        </div>
        {error && <div className="mt-6 rounded-xl border border-danger/25 bg-danger/5 p-4 text-sm text-danger" role="alert">{error}</div>}
        <form onSubmit={submit} className="mt-6 space-y-4" autoComplete="off">
          <PasswordField label="Nueva contraseña" value={password} onChange={setPassword} autoFocus describedBy="requisitos-clave" />
          <ul id="requisitos-clave" className="grid gap-1.5 rounded-xl bg-background p-4 text-sm" aria-label="Requisitos de la contraseña">
            {requirements.map(([label, test]) => {
              const ok = test(password)
              return <li key={label} className={'flex items-center gap-2 ' + (ok ? 'text-success-text' : 'text-text-secondary')}><span className={'grid h-5 w-5 shrink-0 place-items-center rounded-full text-[11px] font-bold ' + (ok ? 'bg-success text-white' : 'border border-border')} aria-hidden="true">{ok ? '✓' : ''}</span>{label}<span className="sr-only">{ok ? ' (cumplido)' : ' (pendiente)'}</span></li>
            })}
          </ul>
          <PasswordField label="Confirmar nueva contraseña" value={confirmation} onChange={setConfirmation} />
          {confirmation && password !== confirmation && <p className="text-sm text-danger">Las contraseñas no coinciden.</p>}
          <button disabled={saving} className="min-h-12 w-full rounded-xl bg-primary px-4 py-3 font-semibold text-white shadow-lg shadow-primary/15 transition hover:bg-primary-hover disabled:opacity-60">{saving ? 'Guardando…' : 'Cambiar contraseña'}</button>
        </form>
        <button type="button" onClick={logout} className="mx-auto mt-4 flex min-h-11 items-center justify-center gap-2 px-4 text-sm font-semibold text-text-secondary hover:text-primary"><Icon name="logout" className="h-4 w-4" />Cerrar sesión</button>
      </section>
    </main>
  )
}
